<template>
  <div
    v-if="editableComposedSD"
    class="scene-variables-container">
    <!-- Header stays visible while the scene data list scrolls -->
    <div class="header">
      <v-alert
        v-if="isLiveBool"
        variant="tonal"
        color="info"
        icon="mdi-gesture-double-tap"
        prominent
        title="You are editing a Live Scene"
        class="mb-3">
        <v-row
          align="center"
          class="pa-0 ma-0">
          <v-col
            cols="auto"
            class="py-0 my-0"
            style="display: flex; align-items: center">
            <v-switch
              persistent-hint
              hint="By enabling live editing, any changes made here will be reflected immediately in the live output (similar to Interact mode in OnAir)."
              v-model="liveSwitch"
              :label="
                'Live Scene Data Editing is ' +
                (liveSwitch ? 'Enabled' : 'Disabled')
              "
              color="info"
              class="pa-0" />
          </v-col>
        </v-row>
      </v-alert>

      <div
        v-if="liveSwitch"
        class="preview-wrap">
        <PreviewDisplay
          :disableInteractivity="true"
          :frameNo="state.selected_frame"
          :disablePositionalBorderRadius="true" />
      </div>
    </div>

    <!-- Scrollable scene data area -->
    <div class="scene-data-scroll">
      <SceneData
        :minusHeight="minusHeight()"
        :hide_activate_controls="true"
        originLevel="scene"
        :originObj="immutableComposedSD"
        :hideicons="true"
        :svars="editableComposedSD"
        resetmsg="Reset to Template Default"
        :templateUid="userEditObj.scene_name"
        :src_svars="findTemplate(userEditObj.scene_name).src_scene_data"
        @valueChanged="onSceneDataChanged" />
    </div>
  </div>
</template>
<script setup>
import { ref, readonly, watch, computed, onMounted } from 'vue';
import { deluge, state } from '../../../stores/deluge';
import SceneData from '../../Shared/SceneData.vue';
import { composeSceneDataFromScene } from '../../../utils/sceneDataComposition';
import { $http } from '../../../stores/http';
import PreviewDisplay from '../../OnAir/CueTake/PreviewDisplay.vue';
const props = defineProps(['userEditObj', 'isLive', 'channel_did']);

function minusHeight() {
  if (isLiveBool.value && liveSwitch.value) {
    return 370;
  } else if (isLiveBool.value && !liveSwitch.value) {
    return 138;
  } else {
    return 0;
  }
}

// local reactive to show switch — default OFF
const liveSwitch = ref(false);

// normalized boolean for isLive prop (handles ref or primitive)
const isLiveBool = computed(() => {
  return typeof props.isLive === 'object'
    ? !!props.isLive.value
    : !!props.isLive;
});

// Ensure switch is off when scene becomes not-live
watch(
  () => isLiveBool.value,
  (val) => {
    if (!val) liveSwitch.value = false;
  }
);

function getStorageKey() {
  const ch =
    typeof props.channel_did === 'object'
      ? props.channel_did.value
      : props.channel_did;
  return `cpro_live_edit_${ch}`;
}

// Load persisted setting on mount
onMounted(() => {
  try {
    const key = getStorageKey();
    const stored = localStorage.getItem(key);
    if (stored !== null) {
      liveSwitch.value = stored === 'true';
    }
  } catch (e) {
    // ignore localStorage errors
  }
  // ensure disabled when not live
  if (!isLiveBool.value) liveSwitch.value = false;
});

// Persist changes to localStorage
watch(
  () => liveSwitch.value,
  (val) => {
    try {
      const key = getStorageKey();
      localStorage.setItem(key, val ? 'true' : 'false');
    } catch (e) {
      // ignore
    }
  }
);

// Handler for SceneData changes — forwards to live scene when liveSwitch is true
async function onSceneDataChanged(msg) {
  // msg is expected to be { key, type, data, portMap }
  if (!liveSwitch.value) {
    // when live edit is off, do nothing special here — SceneData probably modifies local svars only
    return;
  }

  try {
    const { key, type, data, portMap } = msg;

    const url = `/dynamic_engine/scene_data?ch_did=${
      // channel_did may be a ref
      typeof props.channel_did === 'object'
        ? props.channel_did.value
        : props.channel_did
    }&cue=false&port_map=${JSON.stringify(portMap)}`;

    await $http(
      'POST',
      url,
      JSON.stringify({ name: key, type: type, data: data })
    );
  } catch (e) {
    console.error('Failed to send live scene data:', e);
  }
}

// Reset editable data to the immutable/original composition and optionally push to live scene
async function resetSceneData() {
  try {
    // reset local editable svars
    editableComposedSD.value = JSON.parse(JSON.stringify(immutableComposedSD));
    if (!('editableComposedSD' in props.userEditObj)) {
      props.userEditObj.editableComposedSD = editableComposedSD.value;
    } else {
      props.userEditObj.editableComposedSD = editableComposedSD.value;
    }

    // Only perform local reset here. Do not push to the server.
    // If you want to push resets to the live scene, toggle Live Edit and make edits or use the Interact panel.
  } catch (e) {
    console.error('Failed to reset scene data', e);
  }
}

const immutableComposedSD = readonly(
  composeSceneDataFromScene(props.userEditObj, deluge.templates)
);

const editableComposedSD = ref(JSON.parse(JSON.stringify(immutableComposedSD)));

// Compute whether editable state differs from the immutable/original composition
const hasChanges = computed(() => {
  try {
    return (
      JSON.stringify(editableComposedSD.value) !==
      JSON.stringify(immutableComposedSD)
    );
  } catch (e) {
    return false;
  }
});

function findTemplate(scene_name) {
  return deluge.templates?.[scene_name];
}

watch(
  editableComposedSD,
  () => {
    // This doesn't really work as change detection, because we add originState for each object now.
    if (!('editableComposedSD' in props.userEditObj)) {
      props.userEditObj.editableComposedSD = editableComposedSD.value;
    }
  },
  { deep: true }
);
</script>

<style scoped>
.scene-variables-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 10px;
  box-sizing: border-box;
}

.header {
  /* keep header visually separated and above the scroll area */
  z-index: 3;
}

.preview-wrap {
  margin-bottom: 8px;
}

.scene-data-scroll {
  flex: 1 1 auto;
  overflow: auto;
  /* small padding so scrollbars don't overlap content */
  padding-right: 6px;
}
</style>
